/**
 * The loading yard — beat 6's room and the first time the shed opens onto sky.
 *
 * This is still a room, even without a roof. Its apron, shed wall, shut doors
 * and prop collision all live in one `HallSolid` list: `solidMeshes()` draws
 * that list and the future zone assembly passes the exact same array to Rapier.
 * A caller gets visible shell meshes from `solidMeshes(loadingYardSolids(), …)`
 * and colliders by iterating that same `HallSolid[]`; no second collision layout
 * exists for this yard to drift away from what is on screen.
 */

import * as THREE from 'three';
import { BOX_TRAILER_RUNNING_GEAR } from '@/models/env/box-trailer';
import { buildFenceBoards } from '@/models/env/fence-boards';
import { buildFencePost } from '@/models/env/perimeter-fence';
import { buildPalletStack } from '@/models/env/pallet-stack';
import { buildPuddle } from '@/models/env/puddle';
import { RACE_GATE_HINGE_OFFSET } from '@/models/env/race-gate';
import { disposeAsset } from '@/models/materials';
import { getAsset } from '@/models/registry';
import type { AssetDefinition } from '@/models/types';
import type { Fixture, ZoneLightingOptions } from '@/render/lighting';
import { getZone, type ZonePalette } from '@/render/palette';
import { instanceGroup, type Placement } from '@/world/instancing';
import { solidMeshes, type HallSolid } from '@/world/solids';
import { mulberry32 } from '@/audio/prng';

/** The palette zone this room belongs to; beat 6's colour arc names the same id. */
export const YARD_ZONE_ID = 'yard';

/** Every authored extent of the roofless yard, in metres. */
export const YARD = {
  floorY: 0,
  minX: -15,
  maxX: 15,
  minZ: -8,
  maxZ: 14,
  shedWallZ: -8,
  shedWallHeight: 4.2,
  wallThickness: 0.35,
} as const;

/** Continuity from beat 5: she begins inside the row of bins, facing into the yard. */
export const YARD_SPAWN = { x: -10.5, y: 0.25, z: -6.5, facing: 0 } as const;

/** The only non-orthogonal yaw in the yard: the hurriedly parked truck. */
export const TRUCK_ROT = -Math.PI / 2 - 0.35;

/** The rear of the trailer is the one truck datum from which its unit is placed. */
export const TRAILER_REAR_ANCHOR = { x: 6.6, z: 5.6 } as const;

const FENCE_BAY_PITCH = 2.4;
const FENCE_POST_OFFSET = FENCE_BAY_PITCH / 2;
const NORTH_BAY_START_X = -14.4;
const TIMBER_PATCH_INDEX = 7;
const NORTH_LAST_FENCE_INDEX = 10;
const SIDE_BAY_START_Z = -6.8;
const SIDE_BAY_COUNT = 9;
const RACE_PANEL_Z = [-7, -5, -3, -1, 1, 3, 5] as const;
const RACE_PANEL_X = [7, 9] as const;
const DRAIN_SEGMENT_COUNT = 10;

const zone: ZonePalette = (() => {
  const found = getZone(YARD_ZONE_ID);
  if (!found) throw new Error('loading yard: yard zone missing from the palette');
  return found;
})();

function zoneSurface(index: number): number {
  const colour = zone.surfaces[index];
  if (colour === undefined) throw new Error(`loading yard: surface ${index} missing from yard palette`);
  return colour;
}

const APRON = zoneSurface(1);
const SHED_WALL = zoneSurface(2);
const STEEL = zoneSurface(3);
/**
 * Beat 12's grass beyond the gate: the `outside` zone's darkest green, the same
 * turf beat 7's field is painted with (`hedgerow.ts`'s `EARTH`). The dawn's
 * chroma ceiling holds it grey; the green is the field's, not the light's.
 */
const FIELD = (() => {
  const colour = getZone('outside')?.surfaces[0];
  if (colour === undefined) throw new Error('loading yard: outside zone surface 0 missing');
  return colour;
})();

function assetDefinitionOrThrow(id: string): AssetDefinition {
  const asset = getAsset(id);
  if (!asset) throw new Error(`loading yard: asset "${id}" is not registered`);
  return asset;
}

function assetOrThrow(id: string): THREE.Group {
  return assetDefinitionOrThrow(id).create();
}

function spanCentre(min: number, max: number): number {
  return (min + max) / 2;
}

function spanHalf(min: number, max: number): number {
  return (max - min) / 2;
}

function shellBox(
  name: string,
  half: HallSolid['half'],
  pos: HallSolid['pos'],
  colour: number,
  kind: HallSolid['kind'] = 'mineral',
  visible = true,
): HallSolid {
  return { name, half, pos, colour, kind, visible };
}

function requireObject(root: THREE.Object3D, name: string): THREE.Object3D {
  const found = root.getObjectByName(name);
  if (found === undefined) {
    throw new Error(`loading yard: ${root.name || 'asset'} is missing ${name} object`);
  }
  return found;
}

function truckForward(): { x: number; z: number } {
  return { x: Math.sin(TRUCK_ROT), z: Math.cos(TRUCK_ROT) };
}

const trailerAsset = assetDefinitionOrThrow('box-trailer');
const cabAsset = assetDefinitionOrThrow('truck-cab');
const hallDoorAsset = assetDefinitionOrThrow('hall-door');
const palletStackAsset = assetDefinitionOrThrow('pallet-stack');

const TRUCK_FORWARD = truckForward();

/** Derived from the rear anchor and the trailer's own declared length. */
export const TRAILER_AT = {
  x: TRAILER_REAR_ANCHOR.x + TRUCK_FORWARD.x * (trailerAsset.footprint.length / 2),
  z: TRAILER_REAR_ANCHOR.z + TRUCK_FORWARD.z * (trailerAsset.footprint.length / 2),
  rotY: TRUCK_ROT,
} as const;

/**
 * Its coupling remains joined if either truck length or yaw is tuned.
 *
 * **PLUS, not minus.** The trailer's +Z is authored as "toward the tractor
 * unit", so the cab sits one half-trailer plus one half-cab ALONG the shared
 * forward vector — the same vector that put the trailer's rear at the head of
 * the race. Subtracting instead puts the cab at (9.23, 6.56): thirteen metres
 * away, on the far side of its own trailer, standing in open yard.
 *
 * Nothing in the toolchain can see that. It typechecks, it lints, the room
 * boots, and the only witness is a picture — which is why this arrives with the
 * arithmetic written down rather than as an assertion that it is right.
 */
export const TRUCK_CAB_AT = {
  x: TRAILER_AT.x + TRUCK_FORWARD.x * ((trailerAsset.footprint.length + cabAsset.footprint.length) / 2),
  z: TRAILER_AT.z + TRUCK_FORWARD.z * ((trailerAsset.footprint.length + cabAsset.footprint.length) / 2),
  rotY: TRUCK_ROT,
} as const;

/** The ramp lies flat, its named hinge kept for beat 12 rather than baked away. */
export const YARD_RAMP_AT = { x: 7.3, z: TRAILER_REAR_ANCHOR.z, rotY: -Math.PI / 2 } as const;

/** The closed gate across the race, whose leaf beat 12 will turn. */
export const RACE_GATE_AT = { x: 8, z: 6, rotY: 0 } as const;

/**
 * The race gate's hinge, in the world: the asset hangs its leaf
 * `RACE_GATE_HINGE_OFFSET` along its own −X from its origin. Beat 12's leaf
 * collider and Holly's hold station are both derived from this, so the gate
 * she holds and the gate the herd comes through are one transform (D-060).
 */
export const RACE_GATE_HINGE = {
  x: RACE_GATE_AT.x - Math.cos(RACE_GATE_AT.rotY) * RACE_GATE_HINGE_OFFSET,
  z: RACE_GATE_AT.z + Math.sin(RACE_GATE_AT.rotY) * RACE_GATE_HINGE_OFFSET,
} as const;

/**
 * The leaf held open onto the yard: a quarter turn, its free end swung north
 * along the race's west line. **Negative**, because negative yaw is what takes
 * the leaf's +X toward +Z (measured; `race-gate.ts` said the opposite until
 * this beat turned it). Not further: past a quarter turn the leaf closes on the
 * trailer's rear corner, 0.14 m west of the hinge line.
 */
export const RACE_GATE_OPEN_YAW = -Math.PI / 2;

/** The leaf's frame runs from 0.06 to 1.66 m out from the hinge, 1.10 m tall (`race-gate.ts`). */
const RACE_GATE_LEAF = { from: 0.06, to: 1.66, height: 1.1, thickness: 0.08 } as const;

/** Where the leaf's free end stands for a given leaf yaw, in the world. */
export function raceGateLeafTip(leafYaw: number): { x: number; z: number } {
  const yaw = RACE_GATE_AT.rotY + leafYaw;
  return { x: RACE_GATE_HINGE.x + Math.cos(yaw) * RACE_GATE_LEAF.to, z: RACE_GATE_HINGE.z - Math.sin(yaw) * RACE_GATE_LEAF.to };
}

/**
 * The race gate's leaf as a collider at a given leaf yaw — the SAME yaw the
 * room writes to the `leaf` pivot, so what the herd is held behind and what
 * is drawn cannot disagree (D-060). Beat 6's shut leaf is in
 * `loadingYardSolids()` for the night yard; the dawn rooms add theirs live.
 */
export function raceGateLeafSolid(leafYaw: number, name: string): HallSolid {
  const yaw = RACE_GATE_AT.rotY + leafYaw;
  const mid = (RACE_GATE_LEAF.from + RACE_GATE_LEAF.to) / 2;
  return {
    name,
    half: { x: (RACE_GATE_LEAF.to - RACE_GATE_LEAF.from) / 2, y: RACE_GATE_LEAF.height / 2, z: RACE_GATE_LEAF.thickness / 2 },
    pos: { x: RACE_GATE_HINGE.x + Math.cos(yaw) * mid, y: RACE_GATE_LEAF.height / 2, z: RACE_GATE_HINGE.z - Math.sin(yaw) * mid },
    yaw,
    colour: 0,
    kind: 'metal',
    // Drawn by the gate asset itself, which is the live pivot.
    visible: false,
  };
}

/**
 * The vehicle gate, placed against the END of the fence run rather than on the
 * bay grid.
 *
 * It was first put at bay index 11 — one more step of the 2.4 m pitch — which
 * silently assumed the gate is a bay. It is not: it is **4.30 m wide**, so at
 * x 12.0 its left leaf and hinge post reached back to x 9.85 and sat almost a
 * metre inside the last bay and its terminal post. Two solid objects in the same
 * place, in the one part of the perimeter beat 7 has to go through.
 *
 * So it is derived: the last bay's outer edge, plus the gate's own half width,
 * read off the asset. A gate that is re-proportioned moves itself.
 */
const PERIMETER_GATE_AT = {
  x: NORTH_BAY_START_X + FENCE_BAY_PITCH * NORTH_LAST_FENCE_INDEX
    + FENCE_POST_OFFSET + assetDefinitionOrThrow('perimeter-gate').footprint.width / 2,
  z: YARD.maxZ,
} as const;

/**
 * Which state of the yard to build. Beat 6's storm is the default and is
 * built exactly as it always was; `dawn` is beat 12's (`docs/beat-12-plan.md`,
 * *the room, re-dressed*): the vehicle gate stands open, the ramp is at the
 * loading angle, the bins are pushed along the wall, and a paddock of grass
 * lies beyond the gate. Geometry that both states share is not duplicated.
 */
export interface LoadingYardOptions {
  readonly dawn?: boolean;
  /** A live beat can supply the vehicle gate itself, including its hinge collider. */
  readonly vehicleGate?: 'dressed' | 'live';
  /**
   * Dawn only: build the paddock's dressing (its fence, grass and far trees).
   * Default true. `&dress=0` turns it off, and the only reader of that is the
   * census's empty-versus-dressed delta (D-100) — the field's ground is a solid
   * and stays either way, so the delta is the dressing and nothing else.
   */
  readonly paddock?: boolean;
}

/**
 * The open vehicle gate: the gap between its two hinge posts, which is the
 * way out beat 12 closes on. `x` spans the leaves' hinge stiles, so a body
 * whose centre is inside it and past `z` has gone through the gate rather than
 * over the fence beside it (the fence has a collider; nothing else could).
 */
export const VEHICLE_GATE = {
  minX: PERIMETER_GATE_AT.x - 2.0,
  maxX: PERIMETER_GATE_AT.x + 2.0,
  z: PERIMETER_GATE_AT.z,
  centreX: PERIMETER_GATE_AT.x,
} as const;

/**
 * How far each leaf stands open, radians from shut. Both swing INTO the yard:
 * the truck came in and the leaves were pushed back out of its way. Not a full
 * quarter turn — the right leaf's hinge is on the east fence line, and at π/2
 * it would lie in the fence's own plane and z-fight it.
 */
const GATE_OPEN_LEFT = Math.PI * 0.45;
const GATE_OPEN_RIGHT = Math.PI * 0.44;

/** A perimeter-gate leaf's length and hinge offset, read off `perimeter-gate.ts`. */
const GATE_LEAF_LENGTH = 2.0;
const GATE_HINGE_OFFSET = 2.05;

/**
 * The ramp at the loading angle: its free end rests on the trailer's deck at
 * the trailer's rear. The hinge is the race end (x 8.9: `trailer-ramp.ts` puts
 * it 1.6 m behind the asset origin, and the −π/2 yaw sends local −Z to world
 * +X); the rear is 2.3 m west of it at x 6.6, and the hinge sits 0.09 m up.
 * Negative about local X lifts the free (+Z) end.
 */
export const YARD_DAWN_RAMP_PITCH = -Math.atan2(
  BOX_TRAILER_RUNNING_GEAR.deckY - 0.09,
  YARD_RAMP_AT.x + 1.6 - TRAILER_REAR_ANCHOR.x,
);

/**
 * Continuity from beat 11: the office corridor lets out at the cull door, the
 * door she was wheeled out of in beat 6, and she starts a few paces out of it
 * facing into the yard. The bin row that stood here in beat 6 is pushed west
 * for the dawn (`YARD_DAWN_BIN_ROW`), or she would spawn boxed in behind it.
 *
 * **2.4 m off the wall, not at the leaf.** The first spawn was 0.9 m out, and
 * the spring arm (1.85 m) had nowhere to stand but inside the shed wall: it
 * climbed, and the beat opened looking straight down on her back at the
 * concrete. At 2.4 m the arm fits and the first frame is the yard.
 */
export const YARD_DAWN_SPAWN = { x: -9.5, y: 0.25, z: YARD.shedWallZ + YARD.wallThickness / 2 + 2.4, facing: 0 } as const;

/** The paddock beyond the gate: grass, a wire fence, and the field past it. */
export const YARD_DAWN_PADDOCK = { minX: 3.6, maxX: 22.8, minZ: YARD.maxZ, maxZ: 23.6 } as const;

/**
 * Mark 1's junction box: beat 8's fuse box, the same asset and the same verb,
 * low on the shed wall between the cull door and the shed door. Low because the
 * farm's boxes are at boot height (`fuse-box.ts`), which is her snout height;
 * mid-wall because the driver has to WALK to it in stage 3, and the walk from
 * the ramp foot to here is the clock. The asset faces +Z, which is into the
 * yard off this wall, so it takes no yaw.
 */
export const YARD_DAWN_JUNCTION_BOX = {
  x: -1.5,
  y: 0,
  z: YARD.shedWallZ + YARD.wallThickness / 2 + assetDefinitionOrThrow('fuse-box').footprint.length / 2,
} as const;

/** Where her snout aims for the nudge: the box's face, at its middle height. */
export const YARD_DAWN_JUNCTION_BOX_INTERACT = {
  x: YARD_DAWN_JUNCTION_BOX.x,
  y: 0.18,
  z: YARD_DAWN_JUNCTION_BOX.z + 0.12,
} as const;

/**
 * Where beat 12's route from the box to Holly crosses under the trailer: on its
 * axis, 4.2 m forward of the rear. Mark 1 sends the driver west along the
 * trailer's north side and round the cab to the box, so the long way round
 * meets him head-on (audit row 24); under the trailer she comes out behind
 * him. `cold-spawn.spec.ts`'s (c) beat 12 walks through here.
 */
export const YARD_DAWN_UNDER_TRAILER = {
  x: TRAILER_REAR_ANCHOR.x + TRUCK_FORWARD.x * 4.2,
  z: TRAILER_REAR_ANCHOR.z + TRUCK_FORWARD.z * 4.2,
} as const;

/**
 * Which side of the truck's axis a point is on: positive on the shed side
 * (the box), negative on the race side (the gate, Holly). Metres off the axis.
 */
export function sideOfTruck(x: number, z: number): number {
  return -TRUCK_FORWARD.z * (x - TRAILER_REAR_ANCHOR.x) + TRUCK_FORWARD.x * (z - TRAILER_REAR_ANCHOR.z);
}

/**
 * Mark 3's arrow on her sheet: out of the race gate, across the yard, through
 * the vehicle gate. The line the herd is led along in stage 5 is authored
 * there; this is her drawing of it, so it is three points and not a lane.
 */
export const YARD_DAWN_HERD_ARROW: readonly { x: number; z: number }[] = [
  // Its tail clear of the race-gate mark, which is drawn at the gate itself.
  { x: RACE_GATE_AT.x + 0.8, z: RACE_GATE_AT.z + 2.2 },
  { x: (RACE_GATE_AT.x + VEHICLE_GATE.centreX) / 2 + 0.6, z: (RACE_GATE_AT.z + YARD.maxZ) / 2 + 0.4 },
  { x: VEHICLE_GATE.centreX, z: YARD.maxZ + 1.2 },
];

/**
 * Mark 3's actual lane.  The map arrow starts clear of the race-gate symbol so
 * it can be read as an arrow; the herd cannot: its first body is at the leaf.
 * Keep that extra point here, beside the room coordinates, and give story only
 * the resulting scalar length.  Otherwise a simulation import would make the
 * authored drawing and the path it promises silently diverge.
 */
export const YARD_DAWN_HERD_LANE: readonly { x: number; z: number }[] = [
  { x: 8, z: 6 },
  { x: 8.4, z: 9 },
  { x: VEHICLE_GATE.centreX, z: 13 },
  // Through the gate and away WEST across the paddock, not on north: the
  // close is shot from the paddock's east side, and a lane running straight
  // at the lens piled the stream up in the foreground with her behind it.
  { x: VEHICLE_GATE.centreX - 0.3, z: VEHICLE_GATE.z + 2.0 },
  { x: YARD_DAWN_PADDOCK.minX + 2.9, z: YARD_DAWN_PADDOCK.maxZ - 2.1 },
];

function laneLength(points: readonly { readonly x: number; readonly z: number }[]): number {
  let length = 0;
  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (from === undefined || to === undefined) throw new Error('loading yard: herd lane has a hole');
    length += Math.hypot(to.x - from.x, to.z - from.z);
  }
  return length;
}

/** The coordinate-free story module receives this value, never these points. */
export const DAWN_HERD_LANE_LENGTH = laneLength(YARD_DAWN_HERD_LANE);

/** Where the closing condition lies along the lane: the open vehicle gateway. */
export const DAWN_HERD_GATE_DISTANCE = laneLength(YARD_DAWN_HERD_LANE.slice(0, 3)) + 1;

/** Project a presentation member onto Mark 3's authored polyline. */
export function dawnHerdLanePointAt(distance: number): { x: number; z: number; heading: number } {
  if (distance < 0) return { x: 8, z: 6 + distance, heading: 0 };
  const clamped = Math.min(DAWN_HERD_LANE_LENGTH, Math.max(0, distance));
  let walked = 0;
  for (let index = 1; index < YARD_DAWN_HERD_LANE.length; index += 1) {
    const from = YARD_DAWN_HERD_LANE[index - 1];
    const to = YARD_DAWN_HERD_LANE[index];
    if (from === undefined || to === undefined) throw new Error('loading yard: herd lane has a hole');
    const segment = Math.hypot(to.x - from.x, to.z - from.z);
    if (clamped <= walked + segment || index === YARD_DAWN_HERD_LANE.length - 1) {
      const t = segment === 0 ? 0 : (clamped - walked) / segment;
      return { x: from.x + (to.x - from.x) * t, z: from.z + (to.z - from.z) * t, heading: Math.atan2(to.x - from.x, to.z - from.z) };
    }
    walked += segment;
  }
  throw new Error('loading yard: herd lane has no segment');
}

/** Project Rosalie into the wide lead corridor without giving story any coordinates. */
export function projectOntoDawnHerdLane(at: { readonly x: number; readonly z: number }): { distance: number; offset: number } {
  let best: { distance: number; offset: number } | null = null;
  let walked = 0;
  for (let index = 1; index < YARD_DAWN_HERD_LANE.length; index += 1) {
    const from = YARD_DAWN_HERD_LANE[index - 1];
    const to = YARD_DAWN_HERD_LANE[index];
    if (from === undefined || to === undefined) throw new Error('loading yard: herd lane has a hole');
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const length = Math.hypot(dx, dz);
    const along = length === 0 ? 0 : Math.min(1, Math.max(0, ((at.x - from.x) * dx + (at.z - from.z) * dz) / (length * length)));
    const x = from.x + dx * along;
    const z = from.z + dz * along;
    const candidate = { distance: walked + length * along, offset: Math.hypot(at.x - x, at.z - z) };
    if (best === null || candidate.offset < best.offset) best = candidate;
    walked += length;
  }
  if (best === null) throw new Error('loading yard: herd lane has no segment');
  return best;
}

/** True once her centre is through the open gateway and out of the yard. */
export function isThroughVehicleGate(at: { readonly x: number; readonly z: number }): boolean {
  return at.z > VEHICLE_GATE.z + 0.3 && at.x > VEHICLE_GATE.minX && at.x < VEHICLE_GATE.maxX;
}

/** The intact repair that beat 7 later replaces. */
export const TIMBER_PATCHED_BAY = {
  x: NORTH_BAY_START_X + FENCE_BAY_PITCH * TIMBER_PATCH_INDEX,
  z: YARD.maxZ,
  rotY: Math.PI / 2,
} as const;

/**
 * The trailer's own geometry, in its own axes, **read off `box-trailer.ts`**.
 *
 * ── The bug this exists to fix, and it would have shipped ────────────────────
 *
 * Beat 6 ends with her **under the trailer**, and until session 27 there was no
 * under the trailer: `trailer-solid` was one `placementCollider`, and that
 * helper builds a box from the ground to the asset's full declared height. So
 * the trailer was solid from y 0 to y 3.9, `TRAILER_SHADOW_REGION`'s centre —
 * the point the director sets her down at — was **inside a collider**, and the
 * cover volume `loadingYardStealth.ts` bakes over the same region described a
 * place nothing could stand.
 *
 * Nothing could have caught it before the beat existed. The room booted, the
 * budget gate passed, the containment sweep passed (it pushes at the perimeter),
 * and the mesh was right the whole time — `box-trailer.ts` has a deck frame at
 * 1.09 with 1.03 m of daylight under it, wheels, and landing legs. **Only the
 * collider was a lie**, and the only witness was a beat that did not exist yet.
 * That is the same shape as `CULL_RAIL.y` and `setWear()`: a value authored
 * carefully and never read by the one thing that would have disagreed with it.
 *
 * So the trailer is three boxes rather than one: the body, raised to its real
 * underside; the wheel bogie; and the landing legs. Every number here is read
 * off the asset, and the derivation is written out rather than pasted, so that
 * re-proportioning the trailer moves its colliders with it.
 */
const TRAILER_LOCAL = {
  /** `deck-frame` is a 0.12-deep box centred at 1.09, so daylight starts here. */
  deckY: BOX_TRAILER_RUNNING_GEAR.deckY,
  /**
   * The bogie. Two axles at local z −2.2 and −3.4 carrying 0.5 m tyres 1.115
   * out, so the block is 2.55 across, 2.2 long and one wheel high.
   */
  bogie: BOX_TRAILER_RUNNING_GEAR.bogie,
  /** The landing legs, which hold the nose up while the tractor unit is away. */
  legs: BOX_TRAILER_RUNNING_GEAR.legs,
  /**
   * The space she can actually be in, kept well inside both.
   *
   * **This is not `TRAILER_SHADOW_REGION`**, and the difference is the whole
   * reason `isUnderTrailer` exists. The region is the axis-aligned bounds of a
   * box parked 0.35 rad off square (D-108), so two of its corners are open yard
   * with nothing over them at all. Good enough for a concealment volume, which
   * only has to be roughly where the shadow is; useless as "has she arrived",
   * which is the beat's last stage condition.
   */
  under: { halfX: 1.1, minZ: -1.6, maxZ: 2 },
} as const;

/** The box's declared height, exported so the light grid need not re-state it. */
export const TRAILER_HEIGHT = trailerAsset.footprint.height;

/** Trailer-local metres to world. Its +Z is the shared truck forward vector. */
export function trailerLocalToWorld(lx: number, lz: number): { x: number; z: number } {
  const c = Math.cos(TRUCK_ROT);
  const s = Math.sin(TRUCK_ROT);
  return { x: TRAILER_AT.x + lx * c + lz * s, z: TRAILER_AT.z - lx * s + lz * c };
}

/**
 * Is she actually under it — under the deck, between the legs and the bogie?
 *
 * The inverse of `trailerLocalToWorld`, then a rectangle test in the trailer's
 * own axes. Exact where an AABB is not; see `TRAILER_LOCAL.under`.
 */
export function isUnderTrailer(x: number, z: number): boolean {
  const c = Math.cos(TRUCK_ROT);
  const s = Math.sin(TRUCK_ROT);
  const dx = x - TRAILER_AT.x;
  const dz = z - TRAILER_AT.z;
  const lx = dx * c - dz * s;
  const lz = dx * s + dz * c;
  return Math.abs(lx) <= TRAILER_LOCAL.under.halfX
    && lz >= TRAILER_LOCAL.under.minZ
    && lz <= TRAILER_LOCAL.under.maxZ;
}

/**
 * The middle of the space `isUnderTrailer` accepts, in world metres: where
 * beat 6's crossing turns her when she stalls (D-212). Derived from
 * `TRAILER_LOCAL.under`, so the look and the stage's arrival test cannot drift.
 */
export const UNDER_TRAILER_AT = trailerLocalToWorld(0, (TRAILER_LOCAL.under.minZ + TRAILER_LOCAL.under.maxZ) / 2);

/** The concealment volume, which is the shadow rather than the shelter. */
export const TRAILER_SHADOW_REGION = (() => {
  const halfWidth = trailerAsset.footprint.width / 2;
  const halfLength = trailerAsset.footprint.length / 2;
  const halfX = Math.abs(Math.cos(TRUCK_ROT)) * halfWidth + Math.abs(Math.sin(TRUCK_ROT)) * halfLength;
  const halfZ = Math.abs(Math.sin(TRUCK_ROT)) * halfWidth + Math.abs(Math.cos(TRUCK_ROT)) * halfLength;
  return {
    minX: TRAILER_AT.x - halfX,
    maxX: TRAILER_AT.x + halfX,
    minZ: TRAILER_AT.z - halfZ,
    maxZ: TRAILER_AT.z + halfZ,
  } as const;
})();

const CULL_DOOR_AT = { x: -9.5, z: YARD.shedWallZ + YARD.wallThickness / 2 } as const;
const SHED_DOOR_AT = { x: 8.5, z: YARD.shedWallZ + YARD.wallThickness / 2 } as const;
const DOORWAY_HALF = hallDoorAsset.footprint.width / 2;
const DOORWAY_HEIGHT = hallDoorAsset.footprint.height;
const YARD_FLOODLIGHT_AT = { x: 11, z: 2, rotY: -Math.PI / 2 } as const;
const SHED_LAMP_AT = { x: SHED_DOOR_AT.x, y: 2.5, z: -7.35 } as const;

/**
 * What `observe` is aimed at on him: the board, not his face.
 *
 * Chest height on a 1.8 m man standing at the foot of the race, which is where
 * `focus` composes when the player holds it — and the composition is the point.
 * The plan's third rule keeps every camera in beat 6 at least 4 m off his face;
 * aiming the reframe at his hands keeps that true even at the closest the player
 * can get, and it puts the clipboard in the middle of the frame, which is what
 * they are being shown.
 *
 * Exported for `wayGoals.ts`: beat 6's count turns her to it (D-212).
 */
export const KESTNER_OBSERVE_AT = { x: 10.2, y: 1.15, z: 0.8 } as const;

export const PALLET_STACKS = [
  { x: -12, z: -3.5, levels: 11 },
  { x: -9.5, z: 0.5, levels: 7 },
  { x: -6.5, z: 3, levels: 4 },
  { x: -12.5, z: 4.5, levels: 7 },
  { x: -4, z: -1, levels: 11 },
] as const;

/**
 * The bin row's line, exported because the BEAT hangs a stage on it.
 *
 * Stage 1 ends when she is two metres clear of the bins, and `main.ts` measures
 * that off this rather than off a literal −6.5 — moving the bins has to move the
 * stage's exit condition with them. `CULL_RAIL.y` is this project's standing
 * example of what a second statement of one dimension costs.
 */
export const BIN_ROW_Z = -6.5;

export const BIN_ROW = [-11, -10, -9, -8].map((x) => ({ x, z: BIN_ROW_Z })) as readonly Placement[];

/** Beat 12's bins: the same row, pushed west along the wall and clear of the cull door. */
export const YARD_DAWN_BIN_ROW = [-14.2, -13.2, -12.2, -11.2].map((x) => ({ x, z: BIN_ROW_Z })) as readonly Placement[];

function binRowFor(options: LoadingYardOptions): readonly Placement[] {
  return options.dawn === true ? YARD_DAWN_BIN_ROW : BIN_ROW;
}

export const PUDDLES = [
  { x: -8, z: -4, radius: 1.6, seed: 3 },
  { x: -2.5, z: 1.5, radius: 1.2, seed: 17 },
  { x: 3, z: -3, radius: 1.8, seed: 29 },
  { x: -11.5, z: 2, radius: 1, seed: 41 },
  { x: 6, z: -5.5, radius: 1.4, seed: 53 },
  { x: 0.5, z: 8, radius: 1.3, seed: 67 },
] as const;

function northFencePlacements(): Placement[] {
  const placements: Placement[] = [];
  for (let index = 0; index <= NORTH_LAST_FENCE_INDEX; index += 1) {
    if (index === TIMBER_PATCH_INDEX) continue;
    placements.push({ x: NORTH_BAY_START_X + FENCE_BAY_PITCH * index, z: YARD.maxZ, rotY: Math.PI / 2 });
  }
  return placements;
}

function sideFencePlacements(x: number): Placement[] {
  const placements: Placement[] = [];
  for (let index = 0; index < SIDE_BAY_COUNT; index += 1) {
    placements.push({ x, z: SIDE_BAY_START_Z + FENCE_BAY_PITCH * index });
  }
  return placements;
}

const TERMINAL_POSTS: readonly Placement[] = [
  // North bays own their local -Z posts, which are world -X after π/2; this is
  // the final +X post after the last fence bay before the vehicle gate.
  { x: NORTH_BAY_START_X + FENCE_BAY_PITCH * NORTH_LAST_FENCE_INDEX + FENCE_POST_OFFSET, z: YARD.maxZ, rotY: Math.PI / 2 },
  // West bays are unrotated, so their far local +Z end is z = 13.6.
  { x: YARD.minX, z: SIDE_BAY_START_Z + FENCE_BAY_PITCH * (SIDE_BAY_COUNT - 1) + FENCE_POST_OFFSET },
  // East uses the same local-Z run and therefore needs its one terminal at 13.6 too.
  { x: YARD.maxX, z: SIDE_BAY_START_Z + FENCE_BAY_PITCH * (SIDE_BAY_COUNT - 1) + FENCE_POST_OFFSET },
];

function rotatedColliderHalf(footprint: AssetDefinition['footprint'], rotY = 0): { x: number; y: number; z: number } {
  const localX = footprint.width / 2;
  const localZ = footprint.length / 2;
  return {
    x: Math.abs(Math.cos(rotY)) * localX + Math.abs(Math.sin(rotY)) * localZ,
    y: footprint.height / 2,
    z: Math.abs(Math.sin(rotY)) * localX + Math.abs(Math.cos(rotY)) * localZ,
  };
}

function placementCollider(
  name: string,
  placement: Placement,
  footprint: AssetDefinition['footprint'],
): HallSolid {
  const half = rotatedColliderHalf(footprint, placement.rotY);
  return shellBox(
    name,
    half,
    // A Placement's y is ground contact; a HallSolid's y is its box centre.
    // This conversion is what keeps an authored prop and its physical body on
    // the same floor, rather than sinking every collider half underground.
    { x: placement.x, y: (placement.y ?? 0) + half.y, z: placement.z },
    STEEL,
    'metal',
    false,
  );
}

/** An AABB half-extent for a trailer-local box, rotated into world axes. */
function trailerRotatedHalf(halfX: number, halfZ: number): { x: number; z: number } {
  return {
    x: Math.abs(Math.cos(TRUCK_ROT)) * halfX + Math.abs(Math.sin(TRUCK_ROT)) * halfZ,
    z: Math.abs(Math.sin(TRUCK_ROT)) * halfX + Math.abs(Math.cos(TRUCK_ROT)) * halfZ,
  };
}

/**
 * The trailer as three colliders, so that there is a space under it.
 *
 * See `TRAILER_LOCAL` for what was wrong before this existed. The body is the
 * only one of the three that uses the asset's full footprint, and it starts at
 * the deck's underside rather than at the ground.
 *
 * **The bogie and the legs are deliberately coarse.** Each is the axis-aligned
 * bounds of a rotated block, which over-states both by a few tens of
 * centimetres — she cannot squeeze between two wheels. That is the right trade:
 * the alternative is convex hulls for a pair of tyres, and the thing this beat
 * actually needs is that the middle of the trailer is open and its two ends are
 * not.
 */
function trailerSolids(): HallSolid[] {
  const footprint = trailerAsset.footprint;
  const body = trailerRotatedHalf(footprint.width / 2, footprint.length / 2);
  const bodyHalfY = (footprint.height - TRAILER_LOCAL.deckY) / 2;
  const bogie = trailerLocalToWorld(0, TRAILER_LOCAL.bogie.z);
  const bogieHalf = trailerRotatedHalf(TRAILER_LOCAL.bogie.halfX, TRAILER_LOCAL.bogie.halfZ);
  const legs = trailerLocalToWorld(0, TRAILER_LOCAL.legs.z);
  const legsHalf = trailerRotatedHalf(TRAILER_LOCAL.legs.halfX, TRAILER_LOCAL.legs.halfZ);
  return [
    shellBox(
      'trailer-body-solid',
      { x: body.x, y: bodyHalfY, z: body.z },
      { x: TRAILER_AT.x, y: TRAILER_LOCAL.deckY + bodyHalfY, z: TRAILER_AT.z },
      STEEL,
      'metal',
      false,
    ),
    shellBox(
      'trailer-bogie-solid',
      { x: bogieHalf.x, y: TRAILER_LOCAL.bogie.top / 2, z: bogieHalf.z },
      { x: bogie.x, y: TRAILER_LOCAL.bogie.top / 2, z: bogie.z },
      STEEL,
      'metal',
      false,
    ),
    shellBox(
      'trailer-legs-solid',
      { x: legsHalf.x, y: TRAILER_LOCAL.legs.top / 2, z: legsHalf.z },
      { x: legs.x, y: TRAILER_LOCAL.legs.top / 2, z: legs.z },
      STEEL,
      'metal',
      false,
    ),
  ];
}

/** The world centre of a perimeter-gate leaf swung `angle` into the yard. */
function openLeafCentre(side: 'left' | 'right', angle: number): { x: number; z: number } {
  const hingeX = PERIMETER_GATE_AT.x + (side === 'left' ? -GATE_HINGE_OFFSET : GATE_HINGE_OFFSET);
  const along = side === 'left' ? 1 : -1;
  const yaw = side === 'left' ? angle : -angle;
  // A leaf's local +X (left) or −X (right) run, turned by the pivot's yaw.
  return {
    x: hingeX + along * Math.cos(yaw) * GATE_LEAF_LENGTH / 2,
    z: PERIMETER_GATE_AT.z - along * Math.sin(yaw) * GATE_LEAF_LENGTH / 2,
  };
}

/**
 * The two open leaves as solids, yawed with the pivots `dressLoadingYard` turns.
 * Both read `GATE_OPEN_*`, so the drawn leaf and the one she bumps into are the
 * same angle by construction (D-060's rule for a static transform).
 */
function openGateLeafSolids(height: number): HallSolid[] {
  return (['left', 'right'] as const).map((side) => vehicleGateLeafSolid(side, side === 'left' ? GATE_OPEN_LEFT : GATE_OPEN_RIGHT, height));
}

/** How far each vehicle-gate leaf stands open at dawn, radians from shut, into the yard. */
export const VEHICLE_GATE_OPEN_ANGLE = { left: GATE_OPEN_LEFT, right: GATE_OPEN_RIGHT } as const;

/**
 * One vehicle-gate leaf as a solid at `angle` radians from shut (into the
 * yard) — the SAME angle a room writes to the asset's `leaf-left`/`leaf-right`
 * pivot (with the right leaf's sign flipped, as `openPerimeterGate` does), so
 * a leaf that swings keeps its collider and its drawing on one number (D-060).
 * Beat 13's jam is the first leaf that moves.
 */
export function vehicleGateLeafSolid(
  side: 'left' | 'right',
  angle: number,
  height = assetDefinitionOrThrow('perimeter-gate').footprint.height,
  name = `perimeter-gate-leaf-${side}-solid`,
): HallSolid {
  const centre = openLeafCentre(side, angle);
  return {
    ...shellBox(
      name,
      { x: GATE_LEAF_LENGTH / 2, y: height / 2, z: 0.04 },
      { x: centre.x, y: height / 2, z: centre.z },
      STEEL,
      'metal',
      false,
    ),
    yaw: side === 'left' ? angle : -angle,
  };
}

/** Where a vehicle-gate leaf's free end is at `angle` from shut: the tip Kestner pushes. */
export function vehicleGateLeafTip(side: 'left' | 'right', angle: number): { x: number; z: number } {
  const hingeX = PERIMETER_GATE_AT.x + (side === 'left' ? -GATE_HINGE_OFFSET : GATE_HINGE_OFFSET);
  const along = side === 'left' ? 1 : -1;
  const yaw = side === 'left' ? angle : -angle;
  return { x: hingeX + along * Math.cos(yaw) * GATE_LEAF_LENGTH, z: PERIMETER_GATE_AT.z - along * Math.sin(yaw) * GATE_LEAF_LENGTH };
}

/**
 * Beyond the open gate: the field's ground, and the paddock's wire fence as
 * three boxes (drawn by the instanced bays, as the yard's own perimeter is).
 *
 * The ground slab is wide — its far edge 380 m out, deep in the haze — and
 * sits 3 cm under the apron: it is the horizon's floor as much as the paddock's, so the dawn sky meets grass rather than the
 * void under a room that was only ever seen at night in rain. Where it runs
 * under the yard the apron covers it.
 */
function paddockSolids(fenceHeight: number, fenceHalfThickness: number): HallSolid[] {
  const p = YARD_DAWN_PADDOCK;
  return [
    shellBox(
      'field-dawn',
      { x: 220, y: 0.1, z: 200 },
      { x: 0, y: -0.13, z: 180 },
      FIELD,
      'organic',
    ),
    shellBox('paddock-north', { x: spanHalf(p.minX, p.maxX), y: fenceHeight / 2, z: fenceHalfThickness }, { x: spanCentre(p.minX, p.maxX), y: fenceHeight / 2, z: p.maxZ }, STEEL, 'metal', false),
    shellBox('paddock-west', { x: fenceHalfThickness, y: fenceHeight / 2, z: spanHalf(p.minZ, p.maxZ) }, { x: p.minX, y: fenceHeight / 2, z: spanCentre(p.minZ, p.maxZ) }, STEEL, 'metal', false),
    shellBox('paddock-east', { x: fenceHalfThickness, y: fenceHeight / 2, z: spanHalf(p.minZ, p.maxZ) }, { x: p.maxX, y: fenceHeight / 2, z: spanCentre(p.minZ, p.maxZ) }, STEEL, 'metal', false),
    // The paddock's south side east of the yard. The yard's north fence and the
    // gate close x 3.6…15; from the yard's east corner to the paddock's there
    // was nothing, and she could walk out of the paddock onto the open field
    // (`docs/playthrough-audit.md` row 4). Drawn by `paddockSouthPlacements()`.
    // Gate: the paddock probe in `cold-spawn.spec.ts`, watched red without it.
    shellBox('paddock-south', { x: spanHalf(YARD.maxX, p.maxX), y: fenceHeight / 2, z: fenceHalfThickness }, { x: spanCentre(YARD.maxX, p.maxX), y: fenceHeight / 2, z: p.minZ }, STEEL, 'metal', false),
  ];
}

/**
 * Every visible shell box and every physical yard obstruction.
 *
 * The apron stays flat even though the drain asset itself falls: introducing a
 * 0.8 % slope here would make this game's first sloped collider, so beat 7 owns
 * that physics change while this dressing gives the puddles their cause now.
 */
export function loadingYardSolids(options: LoadingYardOptions = {}): HallSolid[] {
  const dawn = options.dawn === true;
  const solids: HallSolid[] = [];
  const wallHalf = YARD.wallThickness / 2;

  solids.push(
    shellBox(
      'floor-yard',
      { x: spanHalf(YARD.minX, YARD.maxX), y: 0.1, z: spanHalf(YARD.minZ, YARD.maxZ) },
      { x: spanCentre(YARD.minX, YARD.maxX), y: YARD.floorY - 0.1, z: spanCentre(YARD.minZ, YARD.maxZ) },
      APRON,
      // Dry at dawn: the rain has stopped. `wet` is a sheen, and under the
      // dawn's low key and daylight exposure it came back as a white disc the
      // size of a truck that followed the camera across the floor (the specular
      // lobe, cut by the trailer's shadow). The puddles stay; they are assets.
      dawn ? 'mineral' : 'wet',
    ),
  );

  const cullDoorMin = CULL_DOOR_AT.x - DOORWAY_HALF;
  const cullDoorMax = CULL_DOOR_AT.x + DOORWAY_HALF;
  const shedDoorMin = SHED_DOOR_AT.x - DOORWAY_HALF;
  const shedDoorMax = SHED_DOOR_AT.x + DOORWAY_HALF;
  const wallCentreZ = YARD.shedWallZ;

  // These three solids are deliberately interrupted by two real gaps. A hidden
  // decorative door in one continuous wall would leave collision shut when the
  // screenshot says an opening exists, the failure `cullCorridorSolids()` avoids.
  solids.push(
    shellBox('shed-wall-west', { x: spanHalf(YARD.minX, cullDoorMin), y: YARD.shedWallHeight / 2, z: wallHalf }, { x: spanCentre(YARD.minX, cullDoorMin), y: YARD.shedWallHeight / 2, z: wallCentreZ }, SHED_WALL),
    shellBox('shed-wall-middle', { x: spanHalf(cullDoorMax, shedDoorMin), y: YARD.shedWallHeight / 2, z: wallHalf }, { x: spanCentre(cullDoorMax, shedDoorMin), y: YARD.shedWallHeight / 2, z: wallCentreZ }, SHED_WALL),
    shellBox('shed-wall-east', { x: spanHalf(shedDoorMax, YARD.maxX), y: YARD.shedWallHeight / 2, z: wallHalf }, { x: spanCentre(shedDoorMax, YARD.maxX), y: YARD.shedWallHeight / 2, z: wallCentreZ }, SHED_WALL),
    shellBox('shed-wall-cull-lintel', { x: DOORWAY_HALF, y: spanHalf(DOORWAY_HEIGHT, YARD.shedWallHeight), z: wallHalf }, { x: CULL_DOOR_AT.x, y: spanCentre(DOORWAY_HEIGHT, YARD.shedWallHeight), z: wallCentreZ }, SHED_WALL),
    shellBox('shed-wall-yard-lintel', { x: DOORWAY_HALF, y: spanHalf(DOORWAY_HEIGHT, YARD.shedWallHeight), z: wallHalf }, { x: SHED_DOOR_AT.x, y: spanCentre(DOORWAY_HEIGHT, YARD.shedWallHeight), z: wallCentreZ }, SHED_WALL),
    placementCollider('cull-door-solid', CULL_DOOR_AT, hallDoorAsset.footprint),
    placementCollider('shed-door-solid', SHED_DOOR_AT, hallDoorAsset.footprint),
  );

  solids.push(
    ...trailerSolids(),
    placementCollider('truck-cab-solid', TRUCK_CAB_AT, cabAsset.footprint),
  );
  for (const [index, stack] of PALLET_STACKS.entries()) {
    const height = palletStackAsset.footprint.height * (stack.levels / 7);
    solids.push(shellBox(
      `pallet-stack-solid-${index + 1}`,
      { x: palletStackAsset.footprint.width / 2, y: height / 2, z: palletStackAsset.footprint.length / 2 },
      { x: stack.x, y: height / 2, z: stack.z },
      STEEL,
      'organic',
      false,
    ));
  }

  const raceMinZ = RACE_PANEL_Z[0] - 1;
  const raceMaxZ = RACE_PANEL_Z[RACE_PANEL_Z.length - 1]! + 1;
  for (const [index, x] of RACE_PANEL_X.entries()) {
    solids.push(shellBox(
      `race-run-solid-${index + 1}`,
      { x: 0.04, y: 1.15 / 2, z: spanHalf(raceMinZ, raceMaxZ) },
      { x, y: 1.15 / 2, z: spanCentre(raceMinZ, raceMaxZ) },
      STEEL,
      'metal',
      false,
    ));
  }

  // The race gate, shut, in the night yard only. The dawn rooms own this leaf
  // as a live collider (beat 12 opens it, beat 13 opens with it open), so it
  // cannot be in their static list. Beat 6 had nothing here: the leaf was
  // drawn shut across the race mouth and she walked through it into the race
  // (playthrough audit row 14; `cold-spawn.spec.ts` "(b) beat 6").
  if (!dawn) solids.push(raceGateLeafSolid(0, 'race-gate-leaf-shut'));

  // ── The perimeter, and it had none ─────────────────────────────────────────
  //
  // The fence was instanced as dressing and nothing stopped her at it, so the
  // yard's three open sides were a suggestion: she could walk out of the room in
  // any direction that was not the shed wall. `docs/beat-6-plan.md` asks for the
  // perimeter "built now, **collidable now**, not opened in beat 6" — beat 7
  // exits through it, and an exit that is already open is not an exit.
  //
  // One box per run rather than one per bay: a 30 m fence is a straight line and
  // twenty-eight colliders would buy nothing over three. The gate is a fourth,
  // shut, and it stays shut until beat 7 opens it.
  const fenceHalfThickness = 0.05;
  const fenceHeight = 2.15;
  const perimeterGate = assetDefinitionOrThrow('perimeter-gate');
  const northFenceEnd = dawn ? VEHICLE_GATE.minX : YARD.maxX;
  solids.push(
    // At dawn the north run stops at the gate's left hinge post: the gate is
    // open, and a box across the gap would be a shut gate nobody can see.
    shellBox(
      'perimeter-north',
      { x: spanHalf(YARD.minX, northFenceEnd), y: fenceHeight / 2, z: fenceHalfThickness },
      { x: spanCentre(YARD.minX, northFenceEnd), y: fenceHeight / 2, z: YARD.maxZ },
      STEEL,
      'metal',
      false,
    ),
    shellBox(
      'perimeter-west',
      { x: fenceHalfThickness, y: fenceHeight / 2, z: spanHalf(YARD.minZ, YARD.maxZ) },
      { x: YARD.minX, y: fenceHeight / 2, z: spanCentre(YARD.minZ, YARD.maxZ) },
      STEEL,
      'metal',
      false,
    ),
    shellBox(
      'perimeter-east',
      { x: fenceHalfThickness, y: fenceHeight / 2, z: spanHalf(YARD.minZ, YARD.maxZ) },
      { x: YARD.maxX, y: fenceHeight / 2, z: spanCentre(YARD.minZ, YARD.maxZ) },
      STEEL,
      'metal',
      false,
    ),
  );
  if (dawn) {
    if (options.vehicleGate !== 'live') solids.push(...openGateLeafSolids(perimeterGate.footprint.height));
    solids.push(...paddockSolids(fenceHeight, fenceHalfThickness));
  } else {
    solids.push(shellBox(
      'perimeter-gate-solid',
      { x: perimeterGate.footprint.width / 2, y: perimeterGate.footprint.height / 2, z: fenceHalfThickness },
      { x: PERIMETER_GATE_AT.x, y: perimeterGate.footprint.height / 2, z: PERIMETER_GATE_AT.z },
      STEEL,
      'metal',
      false,
    ));
  }

  const binAsset = assetDefinitionOrThrow('wheeled-bin');
  const binRow = binRowFor(options);
  const binMinX = binRow[0]?.x;
  const binMaxX = binRow[binRow.length - 1]?.x;
  if (binMinX === undefined || binMaxX === undefined) throw new Error('loading yard: bin row missing');
  solids.push(shellBox(
    'bin-row-solid',
    { x: spanHalf(binMinX - binAsset.footprint.width / 2, binMaxX + binAsset.footprint.width / 2), y: binAsset.footprint.height / 2, z: binAsset.footprint.length / 2 },
    { x: spanCentre(binMinX, binMaxX), y: binAsset.footprint.height / 2, z: binRow[0]!.z },
    STEEL,
    'metal',
    false,
  ));

  return solids;
}

function assetAnchorWorldPosition(assetId: string, anchorName: string, placement: Placement): { x: number; y: number; z: number } {
  const asset = assetOrThrow(assetId);
  asset.position.set(placement.x, placement.y ?? 0, placement.z);
  asset.rotation.y = placement.rotY ?? 0;
  asset.updateWorldMatrix(true, true);
  const anchor = requireObject(asset, anchorName);
  const position = anchor.getWorldPosition(new THREE.Vector3());
  // Built only to be measured, so dispose it. `assetOrThrow` runs the factory,
  // and a factory returns fresh geometry and fresh materials every call — three
  // fixtures asking three anchors would otherwise strand three whole assets'
  // worth of GPU buffers at boot, in a room that is already the largest in the
  // game.
  disposeAsset(asset);
  return { x: position.x, y: position.y, z: position.z };
}

/**
 * Four point lights only.
 *
 * D-027 binds this hardware by lights per fragment, especially harshly outdoors
 * where no wall culls the far lights. `render-budget.spec.ts` enforces this
 * four-fixture budget; lightning decorates the baked field later and is not a
 * fifth renderer light.
 */
export function loadingYardFixtures(): Fixture[] {
  return [
    {
      kind: 'point',
      position: assetAnchorWorldPosition('truck-cab', 'headlight-left', TRUCK_CAB_AT),
      // 7.0, not the 2.6 first authored. At 2.6 with the rig's 1.6 falloff the
      // headlights lit a pool the length of the bonnet and nothing else, and
      // the middle of the yard — the thing they exist to make unusable — was as
      // dark as the cover. A headlight is the brightest object in this fiction.
      intensity: 7,
      range: 18,
      name: 'headlight-left',
    },
    {
      kind: 'point',
      position: assetAnchorWorldPosition('truck-cab', 'headlight-right', TRUCK_CAB_AT),
      intensity: 7,
      range: 18,
      name: 'headlight-right',
    },
    {
      kind: 'point',
      position: assetAnchorWorldPosition('yard-floodlight', 'lamp-emitter', YARD_FLOODLIGHT_AT),
      // Weaker than a headlight and mounted four metres up, so it makes a wide
      // soft disc at the race rather than a hard pool. Kestner stands in it.
      intensity: 5,
      range: 13,
      name: 'race-flood',
    },
    {
      kind: 'point',
      position: { ...SHED_LAMP_AT },
      colour: 0xffd2a0,
      intensity: 1.6,
      range: 8,
      name: 'shed-door-lamp',
    },
  ];
}

/** The yard's outdoor rig; the integrator owns when this zone becomes active. */
/**
 * The rig, and its numbers were moved once after looking at the first frames.
 *
 * As first authored — hemi 0.28, ambient 0.10, exposure reference 1.15 — the
 * yard came back very nearly unreadable: the truck, the pallet stacks and the
 * perimeter were silhouettes and nothing else, and the only legible thing in
 * the frame was the pool under a headlight. For a still that is atmospheric.
 * For **this** room it is a design failure, because stage 4 asks the player to
 * cross twenty-two metres by choosing which stack to move to next, and a route
 * you cannot see is not a route you can plan.
 *
 * So the unlit yard sits in the ramp's SECOND band rather than its lowest: the
 * cover reads as shape, and the lit pools are still plainly brighter than it.
 * Darkness in this room is meant to be a place she can be, not a place the
 * player cannot look at — and the storm gets its shock from the flash, which
 * multiplies whatever is here (D-107), not from a floor that starts at black.
 */
export function loadingYardLighting(): ZoneLightingOptions {
  return {
    fixtures: loadingYardFixtures(),
    // Low and from the north-west, so what shadows exist are long. There is no
    // moon in this fiction; this is the sky itself, which is why it is weak and
    // the same hue as the zone key.
    keyDirection: { x: -0.45, y: 0.72, z: 0.3 },
    keyIntensity: 0.34,
    hemiIntensity: 0.42,
    ambientIntensity: 0.18,
    // The yard is 30 x 22 and one extent has to cover all of it.
    shadowExtent: 16,
    // Four practicals at 2.6 to 3.0 deliver well over 1.0 in a pool, so the
    // anchor is set by the AMBIENT half instead: key + hemi + ambient is 0.94,
    // and anchoring just above it is what puts the unlit yard at the top of the
    // second band rather than the bottom of the first. See
    // ZoneLightingOptions.exposureReference for what leaving this at its
    // default does to a room with practicals.
    exposureReference: 1.0,
    // Well under 1 on purpose: `lighting.ts` says an outdoor zone scales the
    // density rather than lowering its `haze`, because `haze` is a palette
    // value describing the AIR. 0.45 haze at 0.25 puts the far side of the yard
    // about half dissolved at 22 m — which is this room's occlusion, since an
    // exterior culls nothing behind walls, and the reason the flash is a shock.
    fogScale: 0.25,
  };
}

/**
 * Beat 12's dawn: the yard's own palette with the light changed, and nothing
 * else (`docs/beat-12-plan.md`, *the room, re-dressed*). `surfaces` is
 * inherited untouched, so the concrete is the concrete — the same rule beat
 * 10's night keeps (`hedgerowNightZone`).
 *
 * Read the sky off it and the reasoning follows: `createSky` paints zenith from
 * `shadow`, the middle from `fill` and the horizon from `fog`. So the dawn is a
 * cold grey-blue sky over a **warm grey horizon** — the sun is not up, the
 * light is coming, and the warm band sits low behind the truck. `COLOUR_ARC`
 * row 12 is chroma 0.24: barely more than the storm's 0.22. This is not the
 * bloom. It is the yard with the rain gone and the sky getting light, and the
 * warmth is a promise the next beat breaks.
 */
export function yardDawnZone(): ZonePalette {
  const yard = getZone('yard');
  if (!yard) throw new Error('loadingYard: yard zone missing from palette');
  return {
    ...yard,
    id: 'yard-dawn',
    label: 'Loading yard at dawn',
    brief: 'grey-blue first light, a warm band low behind the truck, the floods still on',
    // The key is the sky itself, not a sun: cold and pale, with the ramp's full
    // range (the lesson `hedgerowNightZone` paid for — dawn is made by the
    // light's colour, not by turning a white light down).
    key: 0xc3cad4,
    fill: 0x5f6d82,
    shadow: 0x222a36,
    fog: 0xb4a898,
    chromaCeiling: 0.24,
    // Less than the storm's 0.45: morning air, not rain. The far fence reads.
    haze: 0.28,
  };
}

/**
 * The dawn rig. **The headlights are off** — the truck is parked and loading —
 * so two of beat 6's four fixtures remain: the race flood and the lamp over the
 * shed door. Those are the lights mark 1 cuts. No fixture is added (plan:
 * *"No new light"*).
 */
export function yardDawnLighting(): ZoneLightingOptions {
  const storm = loadingYardLighting();
  return {
    ...storm,
    fixtures: loadingYardFixtures()
      .filter((fixture) => fixture.name === 'race-flood' || fixture.name === 'shed-door-lamp')
      .map((fixture) => ({ ...fixture, intensity: fixture.intensity * 4 })),
    // Low, from the east, behind the truck: where the horizon band is.
    keyDirection: { x: 0.7, y: 0.35, z: 0.6 },
    keyIntensity: 0.62,
    hemiIntensity: 0.55,
    ambientIntensity: 0.22,
    // Below the storm's 1.0, and it is the wool shed's part-1 reasoning
    // (`woolShedLighting`): by day the reference is the daylight, not a fitting. At 1.0
    // the apron — the yard's own dark concrete, `surfaces[1]` — came back the
    // night navy of beat 6 under a dawn sky, and neither the key nor the hemi
    // moved it (both swept, 0.15–1.15 and 0.10–0.70: the floor is the paint,
    // not the light). A probe at 0.3 lifted the room and blew the low key's
    // sheen on the wet apron to white; 0.6 keeps the sheen a sheen.
    exposureReference: 0.8,
  };
}

/** Sounds wait for the later ambience pass; this keeps the room seam ready. */
export function loadingYardAmbience(): { id: string; at: { x: number; y: number; z: number }; gain: number }[] {
  return [];
}

function drainPlacements(): Placement[] {
  const drainLength = assetDefinitionOrThrow('drain-channel').footprint.length;
  const start = YARD.minZ + drainLength / 2;
  const end = YARD.maxZ - drainLength / 2;
  const pitch = (end - start) / (DRAIN_SEGMENT_COUNT - 1);
  return Array.from({ length: DRAIN_SEGMENT_COUNT }, (_, index) => ({ x: -11, z: start + pitch * index }));
}

function makeInteractiveAsset(assetId: string, movingPartName: string, name: string): { group: THREE.Group; part: THREE.Object3D } {
  const source = assetOrThrow(assetId);
  source.updateWorldMatrix(true, true);
  const part = requireObject(source, movingPartName);
  const movingSource = part.clone();
  // `instanceGroup` bakes the source group's own transform. The live pivot
  // receives that parent-relative transform below, so the temporary source must
  // begin at its own origin or a raised ramp would be translated twice.
  movingSource.position.set(0, 0, 0);
  movingSource.quaternion.identity();
  movingSource.scale.set(1, 1, 1);
  const partMatrix = part.matrixWorld.clone();
  if (!part.parent) throw new Error(`loading yard: ${assetId} ${movingPartName} has no parent`);
  part.parent.remove(part);
  partMatrix.decompose(part.position, part.quaternion, part.scale);

  // The visible moving portion is still merged through `instanceGroup`, but the
  // named pivot remains live. Replacing this with the raw source would make a
  // second ramp or gate quietly return to per-mesh assembly (D-024).
  const movingMeshes = instanceGroup(movingSource, [{ x: 0, z: 0 }], `${name}-${movingPartName}`);
  part.clear();
  part.add(movingMeshes);

  const group = new THREE.Group();
  group.name = name;
  group.add(instanceGroup(source, [{ x: 0, z: 0 }], `${name}-static`), part);
  return { group, part };
}

/**
 * The vehicle gate as the truck left it: both leaves pushed back into the
 * yard. The leaves are real pivots on the asset, so this turns them before the
 * instancer bakes the group — a dressing transform, not a hinge (the plan:
 * *"A gate that swings is beat 13's problem"*).
 */
function openPerimeterGate(): THREE.Group {
  const gate = assetOrThrow('perimeter-gate');
  requireObject(gate, 'leaf-left').rotation.y = GATE_OPEN_LEFT;
  requireObject(gate, 'leaf-right').rotation.y = -GATE_OPEN_RIGHT;
  gate.updateMatrixWorld(true);
  return gate;
}

/**
 * Where the distant oaks stand, far enough out that the dawn haze takes them.
 * Authored, not sampled (D-045): a screenshot of the same room must match.
 */
const DAWN_FIELD_TREES: readonly Placement[] = [
  { x: -18, z: 46, rotY: 0.4 },
  { x: 6, z: 58, rotY: 2.1 },
  { x: 31, z: 41, rotY: 4.0 },
  { x: 44, z: 62, rotY: 1.2 },
];

/** Tufts over the paddock: a seeded jittered grid, as `hedgerowGrassPlacements` lays them. */
function paddockGrassPlacements(): Placement[] {
  const p = YARD_DAWN_PADDOCK;
  const random = mulberry32(1207);
  const columns = 24;
  const rows = 12;
  const placements: Placement[] = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const x = p.minX + (column + 0.15 + random() * 0.7) / columns * (p.maxX - p.minX);
      const z = p.minZ + 0.4 + (row + 0.15 + random() * 0.7) / rows * (p.maxZ - p.minZ - 0.4);
      placements.push({ x, y: -0.03, z, rotY: random() * Math.PI * 2 });
    }
  }
  return placements;
}

/**
 * The south run, x 15…22.8: 7.8 m, which is not a whole number of 2.4 m bays.
 * Three bays from the yard corner and a fourth ending on the paddock's corner
 * post, overlapping the third by 1.8 m — a multiple of the 0.2 m wire pitch, so
 * the overlap's wires and rail coincide with the third bay's and read as one
 * run with an extra post, not as a double fence or a gap she could see through.
 */
function paddockSouthPlacements(): Placement[] {
  const p = YARD_DAWN_PADDOCK;
  const fromCorner = [0, 1, 2].map((index) => YARD.maxX + FENCE_POST_OFFSET + FENCE_BAY_PITCH * index);
  return [...fromCorner, p.maxX - FENCE_POST_OFFSET].map((x) => ({ x, z: p.minZ, rotY: Math.PI / 2 }));
}

/** The paddock's wire fence (the yard's own bay asset), its grass, and the far trees. */
function dressPaddock(group: THREE.Group): void {
  const p = YARD_DAWN_PADDOCK;
  const northBays = Math.round((p.maxX - p.minX) / FENCE_BAY_PITCH);
  const sideBays = Math.round((p.maxZ - p.minZ) / FENCE_BAY_PITCH);
  const north: Placement[] = Array.from({ length: northBays }, (_, index) => ({
    x: p.minX + FENCE_POST_OFFSET + FENCE_BAY_PITCH * index, z: p.maxZ, rotY: Math.PI / 2,
  }));
  const sides: Placement[] = [p.minX, p.maxX].flatMap((x) => Array.from({ length: sideBays }, (_, index) => ({
    x, z: p.minZ + FENCE_POST_OFFSET + FENCE_BAY_PITCH * index,
  })));
  const paddock = new THREE.Group();
  paddock.name = 'yard-dawn-paddock';
  paddock.add(instanceGroup(assetOrThrow('perimeter-fence'), [...north, ...sides, ...paddockSouthPlacements()], 'yard-dawn-paddock-fence'));
  paddock.add(instanceGroup(buildFencePost(), [
    { x: p.maxX, z: p.maxZ, rotY: Math.PI / 2 },
    { x: p.minX, z: p.maxZ },
  ], 'yard-dawn-paddock-posts'));
  paddock.add(instanceGroup(assetOrThrow('grass-tuft'), paddockGrassPlacements(), 'yard-dawn-grass'));
  paddock.add(instanceGroup(assetOrThrow('field-tree'), DAWN_FIELD_TREES, 'yard-dawn-trees'));
  // None of it casts. The paddock is outside the yard, north of the low east
  // key, so its shadows fall away from everything she plays in — and a shadow
  // pass per batch was a third of the paddock's 28 draw calls, out of the
  // headroom the herd, the crew and Holly have to be built in (stages 3–5).
  paddock.traverse((object) => { object.castShadow = false; });
  group.add(paddock);
}

/** The handles the later yard director will move, with all static dressing merged. */
export interface LoadingYardParts {
  group: THREE.Group;
  /** The ramp's real hinge group; beat 12 raises it from this pivot. */
  ramp: THREE.Object3D;
  /** The race gate's real leaf group; beat 12 swings it from this pivot. */
  raceGateLeaf: THREE.Object3D;
}

/** Dress the shared shell. Every static placement, including one-offs, uses the instancer. */
export function dressLoadingYard(options: LoadingYardOptions = {}): LoadingYardParts {
  const dawn = options.dawn === true;
  const group = new THREE.Group();
  group.name = 'loading-yard-dressing';

  group.add(instanceGroup(assetOrThrow('box-trailer'), [TRAILER_AT], 'yard-trailer'));
  group.add(instanceGroup(assetOrThrow('truck-cab'), [TRUCK_CAB_AT], 'yard-truck-cab'));

  const ramp = makeInteractiveAsset('trailer-ramp', 'hinge', 'yard-ramp');
  ramp.group.position.set(YARD_RAMP_AT.x, 0, YARD_RAMP_AT.z);
  ramp.group.rotation.y = YARD_RAMP_AT.rotY;
  if (dawn) ramp.part.rotation.x = YARD_DAWN_RAMP_PITCH;
  group.add(ramp.group);

  const raceGate = makeInteractiveAsset('race-gate', 'leaf', 'yard-race-gate');
  raceGate.group.position.set(RACE_GATE_AT.x, 0, RACE_GATE_AT.z);
  raceGate.group.rotation.y = RACE_GATE_AT.rotY;
  group.add(raceGate.group);

  group.add(instanceGroup(
    assetOrThrow('race-panel'),
    RACE_PANEL_X.flatMap((x) => RACE_PANEL_Z.map((z) => ({ x, z }))),
    'yard-race-panels',
  ));

  group.add(instanceGroup(assetOrThrow('perimeter-fence'), northFencePlacements(), 'yard-north-fence'));
  group.add(instanceGroup(buildFenceBoards('whole'), [TIMBER_PATCHED_BAY], 'yard-timber-patch'));
  if (options.vehicleGate !== 'live') {
    group.add(instanceGroup(dawn ? openPerimeterGate() : assetOrThrow('perimeter-gate'), [{ x: PERIMETER_GATE_AT.x, z: PERIMETER_GATE_AT.z }], 'yard-perimeter-gate'));
  }
  group.add(instanceGroup(assetOrThrow('perimeter-fence'), sideFencePlacements(YARD.minX), 'yard-west-fence'));
  group.add(instanceGroup(assetOrThrow('perimeter-fence'), sideFencePlacements(YARD.maxX), 'yard-east-fence'));
  group.add(instanceGroup(buildFencePost(), TERMINAL_POSTS, 'yard-terminal-posts'));

  for (const levels of [4, 7, 11] as const) {
    group.add(instanceGroup(
      buildPalletStack(levels),
      PALLET_STACKS.filter((stack) => stack.levels === levels).map(({ x, z }) => ({ x, z })),
      `yard-pallet-stacks-${levels}`,
    ));
  }
  group.add(instanceGroup(assetOrThrow('wheeled-bin'), binRowFor(options), 'yard-bins'));
  if (dawn && options.paddock !== false) dressPaddock(group);
  group.add(instanceGroup(assetOrThrow('drain-channel'), drainPlacements(), 'yard-drain-channel'));
  for (const puddle of PUDDLES) {
    group.add(instanceGroup(buildPuddle({ radius: puddle.radius, seed: puddle.seed }), [{ x: puddle.x, z: puddle.z }], `yard-puddle-${puddle.seed}`));
  }
  group.add(instanceGroup(assetOrThrow('hall-door'), [CULL_DOOR_AT, SHED_DOOR_AT], 'yard-shed-doors'));
  group.add(instanceGroup(assetOrThrow('yard-floodlight'), [YARD_FLOODLIGHT_AT], 'yard-floodlight'));
  group.add(instanceGroup(assetOrThrow('bulkhead-lamp'), [SHED_LAMP_AT], 'yard-shed-lamp'));

  return { group, ramp: ramp.part, raceGateLeaf: raceGate.part };
}

/** Build the un-wired room. Zone lighting and physics consume its exported seams later. */
export function createLoadingYard(options: LoadingYardOptions = {}): LoadingYardParts {
  const room = new THREE.Group();
  room.name = 'loading-yard';
  room.add(solidMeshes(loadingYardSolids(options), 'shell'));
  const dressing = dressLoadingYard(options);
  room.add(dressing.group);
  return { group: room, ramp: dressing.ramp, raceGateLeaf: dressing.raceGateLeaf };
}

/**
 * Rain density for the whole beat, 0..1.
 *
 * A constant, not a cue: it is raining when she climbs out of the bin and it is
 * still raining when she goes under the trailer, and nothing in beat 6 changes
 * it. It lives here rather than in the post stack because it is a property of
 * this ROOM — the workbench and every other zone leave the layer at 0.
 *
 * Not 1.0. At full density the streaks stop being something the yard is seen
 * through and become the thing being looked at, and the one warm shape in the
 * frame is supposed to be her.
 */
export const YARD_RAIN = 0.85;

/**
 * The pallet stack's declared footprint, for anything that needs its extent.
 *
 * Exists so `loadingYardStealth.ts` can size a cover volume from the asset
 * instead of from a second copy of its numbers. Exported rather than inlined
 * because the asset registry is the one place that knows how big a pallet stack
 * is, and every reader should have to go there.
 */
export function palletStackFootprint(): { width: number; height: number; length: number } {
  return palletStackAsset.footprint;
}
